import React, { useEffect, useRef, useState } from "react";
import {
  LuChevronDown,
  LuDownload,
  LuFileSpreadsheet,
  LuFileText,
} from "react-icons/lu";
import { toast } from "react-toastify";

import { GET } from "../../services/axios.service";
import { openDownload } from "../../services/download.service";
import { toastStyles } from "../../static/styles";
import { menuContentClass, menuItemClass } from "../layout/chromeStyles";
import Button from "../ui/Button";

const formats = [
  { value: "xlsx", label: "Excel (.xlsx)", Icon: LuFileSpreadsheet },
  { value: "csv", label: "CSV (.csv)", Icon: LuFileText },
];

type Props = {
  // The module's export API path, e.g. "/v1/admin/categories/export".
  endpoint: string;
  // The filter values the list is fetched with.
  getParams: () => Record<string, unknown>;
};

// Export button for a list's filter card: downloads the filtered records as Excel or CSV.
const ExportMenu = ({ endpoint, getParams }: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleExport = async (format: string) => {
    setOpen(false);
    setIsExporting(true);

    try {
      const response: any = await GET(endpoint, { ...getParams(), format });

      if (response?.data?.status === "success") {
        openDownload(response.data.data.download_link);
      } else {
        toast.error(
          response?.data?.message ||
            "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
        { ...(toastStyles as any) }
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative max-sm:flex-1">
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={isExporting}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <LuDownload className="h-4 w-4" />
        {isExporting ? "ફાઇલ બની રહી છે..." : "એક્સપોર્ટ"}
        {!isExporting && <LuChevronDown className="h-4 w-4" />}
      </Button>

      {open && (
        <div role="menu" className={menuContentClass}>
          {formats.map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              role="menuitem"
              className={menuItemClass}
              onClick={() => handleExport(value)}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
